import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { groupAnalyses } from "../../lib/captureViews";
import { Camera, ArrowRight } from "lucide-react";

/**
 * A student's own motion-capture analyses.
 *
 * This page is what the "New capture ready" notification and push message point
 * at (/student/captures/{id}). Before it existed those links 404'd and the
 * router bounced the athlete back to the dashboard, so a capture a coach sent
 * them was effectively invisible inside the app.
 */
export default function StudentCaptures() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/capture/sessions")
      .then(({ data }) => setRows(Array.isArray(data) ? data : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  // One card per analysis. A two-camera capture is two sessions sharing a
  // `session_group_id`, so listing both reads as two separate analyses of the
  // same strokes. Keep the primary view and show the angle count on the card —
  // the page behind it carries the switcher.
  const collapsed = useMemo(() => groupAnalyses(rows), [rows]);

  return (
    <div className="space-y-6" data-testid="student-captures-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#38BDF8] font-bold">
          Motion capture
        </div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tighter mt-2">
          My analyses
        </h1>
        <p className="text-sm text-[#A3A3A3] mt-1">
          Video form analyses your coach has shared with you.
        </p>
      </div>

      {loading && <div className="text-sm text-[#A3A3A3]">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="card-surface p-12 text-center" data-testid="student-captures-empty">
          <Camera size={22} className="mx-auto text-[#A3A3A3]" />
          <div className="font-display font-bold uppercase tracking-tight mt-3">
            No analyses yet
          </div>
          <p className="text-sm text-[#A3A3A3] mt-1">
            When your coach records a video analysis, it'll show up here.
          </p>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {collapsed.map((s) => (
          <Link
            key={s.id}
            to={`/student/captures/${s.id}`}
            className="card-surface p-5 hover:border-[#38BDF8]/50 transition-colors"
            data-testid={`student-capture-card-${s.id}`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                {s.created_at ? new Date(s.created_at).toLocaleDateString() : ""}
              </div>
              <span className="text-[10px] uppercase tracking-widest font-bold text-[#38BDF8]">
                {s.play_mode || "drill"}
              </span>
            </div>
            <div className="font-display font-bold uppercase tracking-tight mt-2">
              {s.coach_name ? `From ${s.coach_name}` : "Video analysis"}
            </div>
            <p className="text-xs text-[#A3A3A3] mt-2 line-clamp-3">
              {s.ai_preview || "Analysis ready to view."}
            </p>
            {(s.group_size || 1) > 1 && (
              <div
                className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold border border-[#38BDF8]/40 rounded px-2 py-1"
                data-testid={`student-capture-angles-${s.id}`}
              >
                <Camera size={11} /> {s.group_size} angles
              </div>
            )}
            <div className="mt-3 text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold inline-flex items-center gap-1">
              View analysis <ArrowRight size={11} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
